import {
  Activity,
  Camera,
  FileText,
  Gem,
  Layers,
  Lightbulb,
  MessageCircle,
  Package,
  Palette,
  Radio,
  Shield,
  Users,
  type LucideIcon,
} from 'lucide-react';

import type { EventStatus } from '@/config/objects/events';

export interface EventTab {
  label: string;
  path: string;
  icon: LucideIcon;
}

/** Every event tab, keyed for composition into status-specific orders. */
const TABS = {
  overview: { label: 'Overview', path: 'overview', icon: FileText },
  live: { label: 'Live', path: 'live', icon: Radio },
  submissions: { label: 'Submissions', path: 'submissions', icon: Camera },
  challenges: { label: 'Challenges', path: 'challenges', icon: Lightbulb },
  artifacts: { label: 'Artifacts', path: 'artifacts', icon: Gem },
  collectibles: { label: 'Collectibles', path: 'collectibles', icon: Package },
  stacks: { label: 'Stacks', path: 'stacks', icon: Layers },
  teams: { label: 'Teams', path: 'teams', icon: Users },
  alliances: { label: 'Alliances', path: 'alliances', icon: Shield },
  branding: { label: 'Branding', path: 'branding', icon: Palette },
  chat: { label: 'Chat', path: 'chat', icon: MessageCircle },
  activity: { label: 'Activity', path: 'activity', icon: Activity },
} as const satisfies Record<string, EventTab>;

/** Building phase — no Live command center or Submissions queue yet. */
const BUILDING_TABS: readonly EventTab[] = [
  TABS.overview,
  TABS.challenges,
  TABS.artifacts,
  TABS.collectibles,
  TABS.stacks,
  TABS.teams,
  TABS.alliances,
  TABS.branding,
  TABS.chat,
  TABS.activity,
];

/** Running phase — Live first; ops surfaces up front, structure behind. */
const RUNNING_TABS: readonly EventTab[] = [
  TABS.live,
  TABS.overview,
  TABS.submissions,
  TABS.challenges,
  TABS.teams,
  TABS.chat,
  TABS.activity,
  TABS.artifacts,
  TABS.collectibles,
  TABS.stacks,
  TABS.alliances,
  TABS.branding,
];

/** Wrapped up — review surfaces (submissions, activity, final board) first. */
const WRAPPED_TABS: readonly EventTab[] = [
  TABS.overview,
  TABS.submissions,
  TABS.activity,
  TABS.live,
  TABS.teams,
  TABS.challenges,
  TABS.artifacts,
  TABS.collectibles,
  TABS.stacks,
  TABS.alliances,
  TABS.branding,
  TABS.chat,
];

const TABS_BY_STATUS: Record<EventStatus, readonly EventTab[]> = {
  draft: BUILDING_TABS,
  scheduled: BUILDING_TABS,
  active: RUNNING_TABS,
  paused: RUNNING_TABS,
  completed: WRAPPED_TABS,
  archived: WRAPPED_TABS,
};

/**
 * Status-aware tab set for the event detail shell: while running, Live
 * leads; while building, structure leads; once wrapped, review leads.
 * All tab routes stay registered regardless — this only orders the nav.
 *
 * @param status - Current event status (undefined while loading).
 * @returns Ordered tab definitions for EntityDetailShell.
 */
export function getEventTabs(status: EventStatus | undefined): readonly EventTab[] {
  if (!status) return BUILDING_TABS;
  return TABS_BY_STATUS[status] ?? BUILDING_TABS;
}
